import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PublicationExpiresAtField } from '../PublicationExpiresAtField';
import {
  parsePublicationExpiresAtInput,
  toPublicationExpiresAtLocalValue,
} from '../../../utils/publicationExpiresAtUtils';

export interface PublicationExpiresAtModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (publicationExpiresAt: string | null) => void;
  initialValue?: string | null;
  isConfirming?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
}

export const PublicationExpiresAtModal: React.FC<PublicationExpiresAtModalProps> = ({
  open,
  onClose,
  onConfirm,
  initialValue = null,
  isConfirming = false,
  title = 'Confirmar publicação',
  description = 'Defina, se desejar, uma data e hora de validade da publicação. Se informada, deve ser um momento futuro.',
  confirmLabel = 'Confirmar publicação',
}) => {
  const [publicationExpiresAtInput, setPublicationExpiresAtInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open)
    {
      return;
    }

    setPublicationExpiresAtInput(toPublicationExpiresAtLocalValue(initialValue));
    setError(null);
  }, [open, initialValue]);

  const handleConfirm = () => {
    const parsed = parsePublicationExpiresAtInput(publicationExpiresAtInput);
    if (!parsed.ok)
    {
      setError(parsed.message);
      return;
    }

    setError(null);
    onConfirm(parsed.iso);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[60]"
        onClose={() => {
          if (!isConfirming)
          {
            onClose();
          }
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {title}
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-600">
                {description}
              </p>

              <div className="mt-4">
                <PublicationExpiresAtField
                  value={publicationExpiresAtInput}
                  onChange={(value) => {
                    setPublicationExpiresAtInput(value);
                    if (error)
                    {
                      setError(null);
                    }
                  }}
                  disabled={isConfirming}
                />
                {error && (
                  <p className="mt-2 text-sm text-red-600">{error}</p>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isConfirming}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isConfirming}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {isConfirming ? 'Publicando...' : confirmLabel}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
